'use client'

import * as React from 'react'
import { cn } from '@repo/ui'
import { Badge } from '@repo/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/avatar'
import { CallRegular, ChatRegular, ClockRegular, ErrorCircleRegular, MailRegular } from '@fluentui/react-icons'
import { SentimentIconOnly } from '@repo/ai-analytics'
import { useTranslations } from '@repo/core/i18n'
import type { Interaction } from '../../entities/interaction'
import {
  mapLibraryStatusToUi,
  mapLibraryPriorityToUi,
} from '../../entities/interaction/model/types'
import { useInteractionListStoreCompat, pickName } from '../../entities/interaction'
import { useCustomerById } from '../../entities/customer'
import { useCrmLookup } from '../../entities/lookup'
import { formatDistanceToNow } from 'date-fns'
import { ru } from 'date-fns/locale'

interface InteractionCardProps {
  interaction: Interaction
  isSelected?: boolean
  onClick?: () => void
  className?: string
}

/**
 * InteractionCard - Card component for displaying an interaction in the list
 * Memoized to prevent unnecessary re-renders in lists
 */
export const InteractionCard = React.memo(function InteractionCard({
  interaction,
  isSelected = false,
  onClick,
  className,
}: InteractionCardProps) {
  const t = useTranslations('crm.interactionCard')
  // Check for cached AI analysis (no auto-analyze in list view)
  const { analysisCache } = useInteractionListStoreCompat()
  const cachedAnalysis = analysisCache[interaction.id]

  // Resolve customer name through the on-demand resolver: workspace
  // route doesn't pre-load the customer store, so a plain `customers
  // .find(...)` returned undefined and rendered an empty line under
  // the avatar. `useCustomerById` reads the store first, then fires a
  // single deduplicated `crm.customer.get` and pushes the row back
  // into the store so all sibling cards re-render with the resolved
  // name.
  const { customer: linkedCustomer } = useCustomerById(interaction.customerId)
  // Tag chips ship as UUIDs on the wire — cached lookup resolves to labels.
  const { options: tagOptions } = useCrmLookup('tag')
  const tagLabelMap = React.useMemo(
    () => new Map(tagOptions.map((o) => [o.value, o.label])),
    [tagOptions]
  )
  const displayName =
    pickName(interaction.customerName)
    ?? pickName(linkedCustomer?.displayName)
    ?? pickName(linkedCustomer?.name)
    ?? pickName(linkedCustomer?.email)
    ?? pickName(linkedCustomer?.phone)
    ?? t('unknownCustomer')

  // Get icon based on type
  const TypeIcon =
    interaction.type === 'chat' ? ChatRegular : interaction.type === 'call' ? CallRegular : MailRegular

  // Get status badge color + label.
  // Earlier the badge always rendered but `t('status.X')` only returned a
  // string for a 7-status subset — for `resolved/cancelled/missed/in_progress/
  // scheduled/snoozed/completed` the badge was a colored rectangle with no
  // text (the empty grey/colored line under the customer name in the list).
  // Now: skip the badge entirely when we don't have a translation key for it.
  const uiStatus = mapLibraryStatusToUi(interaction.status)
  const statusColor: Record<string, string> = {
    active: 'bg-success/10 text-success border-success/20',
    waiting: 'bg-warning/10 text-warning border-yellow-500/20',
    transferred: 'bg-info/10 text-info border-primary/20',
    closed: 'bg-muted/10 text-muted-foreground border-gray-500/20',
    in_progress: 'bg-info/10 text-info border-primary/20',
    scheduled: 'bg-accent/10 text-accent border-purple-500/20',
    completed: 'bg-success/10 text-success border-success/20',
    cancelled: 'bg-destructive/10 text-destructive border-destructive/20',
    missed: 'bg-destructive/10 text-destructive border-destructive/20',
    open: 'bg-success/10 text-success border-success/20',
    pending: 'bg-warning/10 text-warning border-yellow-500/20',
    resolved: 'bg-muted/10 text-muted-foreground border-gray-500/20',
    snoozed: 'bg-warning/10 text-warning border-yellow-500/20',
    solved: 'bg-muted/10 text-muted-foreground border-gray-500/20', // legacy
  }
  const statusBadgeClass = statusColor[uiStatus] ?? statusColor['open']
  // Translate every known status; fall back to the raw key (capitalised)
  // rather than rendering an empty pill.
  const statusKnownKeys = ['active', 'waiting', 'transferred', 'closed', 'in_progress',
    'scheduled', 'completed', 'cancelled', 'missed', 'open', 'pending', 'resolved',
    'snoozed', 'solved'] as const
  const statusLabel = (statusKnownKeys as readonly string[]).includes(uiStatus)
    ? t(`status.${uiStatus}`)
    : (uiStatus.charAt(0).toUpperCase() + uiStatus.slice(1))

  // Get priority badge color
  const uiPriority = interaction.priority ? mapLibraryPriorityToUi(interaction.priority) : 'normal'
  const priorityColor = {
    urgent: 'bg-destructive/10 text-destructive border-destructive/20',
    high: 'bg-warning/10 text-warning border-orange-500/20',
    normal: 'bg-info/10 text-info border-primary/20',
    low: 'bg-muted/10 text-muted-foreground border-gray-500/20',
  }[uiPriority]

  // Format time
  const timeAgo = interaction.lastMessageAt
    ? formatDistanceToNow(interaction.lastMessageAt, { addSuffix: true, locale: ru })
    : formatDistanceToNow(interaction.createdAt, { addSuffix: true, locale: ru })

  // Get initials for avatar
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div
      onClick={onClick}
      className={cn(
        'hover:bg-muted/50 group relative cursor-pointer border-b p-4 transition-colors',
        isSelected && 'bg-muted',
        className
      )}
    >
      {/* SLA Warning Indicator */}
      {(interaction.slaWarning || interaction.slaExpired) && (
        <div
          className={cn(
            'absolute left-0 top-0 h-full w-1',
            interaction.slaExpired ? 'bg-destructive' : 'bg-warning'
          )}
        />
      )}

      <div className="flex gap-3">
        {/* Avatar */}
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={interaction.customerAvatar} alt={displayName} />
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* Header — name gets full width; sentiment icon is the only
           * top-right element so a long "около 23 часов назад"
           * timeAgo doesn't compress the name. Time moves to the
           * meta line below. */}
          <div className="mb-1 flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h4 className="truncate text-sm font-medium">{displayName}</h4>
              {interaction.title && (
                <p className="text-muted-foreground truncate text-xs">{interaction.title}</p>
              )}
            </div>
            {cachedAnalysis?.sentiment && (
              <div className="shrink-0">
                <SentimentIconOnly label={cachedAnalysis.sentiment.label} size="sm" />
              </div>
            )}
          </div>

          {/* Type + time meta — own line under the subject so the
           * name can use the whole width above. */}
          <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
            <TypeIcon className="h-3.5 w-3.5" />
            <span className="truncate">{timeAgo}</span>
          </div>

          {/* Last Message */}
          {interaction.lastMessage && (
            <p className="text-muted-foreground mb-2 line-clamp-2 text-sm">
              {interaction.lastMessage}
            </p>
          )}

          {/* Footer: Badges and Status */}
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Channel Badge — communicates the medium (chat/call/email)
             * at-a-glance so agents pick the right interaction without
             * opening it. */}
            {interaction.type && (
              <Badge variant="outline" className="gap-1 text-xs capitalize">
                <TypeIcon className="h-3 w-3" />
                {channelLabel(t, interaction.type)}
              </Badge>
            )}

            {/* Status Badge — only rendered when we have a non-empty label */}
            {statusLabel && (
              <Badge variant="outline" className={cn('text-xs', statusBadgeClass)}>
                {statusLabel}
              </Badge>
            )}

            {/* Priority Badge */}
            {uiPriority !== 'normal' && (uiPriority === 'urgent' || uiPriority === 'high' || uiPriority === 'low') && (
              <Badge variant="outline" className={cn('text-xs', priorityColor)}>
                {t(`priority.${uiPriority}`)}
              </Badge>
            )}

            {/* Queue Badge */}
            {interaction.queueName && (
              <Badge color="secondary" className="text-xs">
                {interaction.queueName}
              </Badge>
            )}

            {/* SLA Indicator */}
            {interaction.slaDeadline && !interaction.slaExpired && (
              <div className="text-muted-foreground flex items-center gap-1 text-xs">
                <ClockRegular className="h-3 w-3" />
                <span>
                  {Math.floor(
                    (new Date(interaction.slaDeadline).getTime() - Date.now()) / (1000 * 60)
                  )}{' '}
                  {t('minutesShort')}
                </span>
              </div>
            )}

            {/* SLA Expired */}
            {interaction.slaExpired && (
              <div className="text-destructive flex items-center gap-1 text-xs">
                <ErrorCircleRegular className="h-3 w-3" />
                <span>{t('slaBreached')}</span>
              </div>
            )}

            {/* Unread Count — boolean-coerce so `0` doesn't leak through
             * JS short-circuit and render as the literal text "0". */}
            {(interaction.unreadCount ?? 0) > 0 && (
              <Badge className="bg-primary text-primary-foreground ml-auto">
                {interaction.unreadCount}
              </Badge>
            )}
          </div>

          {/* Tags — wire ships tag UUIDs; resolve to labels via the
              cached lookup so chips show "VIP" not "0193ab…". */}
          {interaction.tags && interaction.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {interaction.tags.slice(0, 3).map((tagId) => (
                <Badge key={tagId} variant="outline" className="text-xs">
                  {tagLabelMap.get(tagId) ?? `${tagId.slice(0, 8)}…`}
                </Badge>
              ))}
              {interaction.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{interaction.tags.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* Assigned Agent */}
          {interaction.assignedToName && (
            <div className="text-muted-foreground mt-2 text-xs">
              {t('assignedTo', { name: interaction.assignedToName })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

// Render `channel.${type}` if the key is present, otherwise fall back
// to the raw type. Our `t()` returns `namespace.key` for missing keys
// (see `packages/core/src/i18n`), so check via the suffix marker.
function channelLabel(t: (k: string) => string, type: string): string {
  const v = t(`channel.${type}`)
  return v.endsWith(`channel.${type}`) ? type : v
}
